import { createSelector } from "@reduxjs/toolkit"
import { Button, ConfigProvider, Modal } from "antd"
import { lazy, Suspense, useEffect, useState } from "react"
import { ErrorBoundary } from "react-error-boundary"
import { Helmet } from "react-helmet-async"
import { useDispatch, useSelector } from "react-redux"
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate
} from "react-router-dom"
import {
  closeLoading,
  closeTimeoutDialog,
  logout,
  openTimeoutDialog
} from "./actions"
import ErrorBoundaryLayout from "./common/ErrorBoundaryLayout"
import {
  clearLoginData,
  getAuth,
  initActivityListener,
  isUserIdle,
  setGlobalLoadingFalse
} from "./common/utils"
import LoadingContainer from "./components/LoadingContainer"
import "./style/leaflet.css"
import "./style/scss/index.scss"
import "./style/tailwind.css"

let available = true // 選擇要build哪一種網頁: 正常版或是維修中(去中辦的話build兩個版本)

// 先宣告等著lazy import
let Login,
  SimpleFilter,
  ModelCreate,
  HouseLand,
  FactorAdjustmentTable,
  LandPriceSearch,
  LandPriceInquiry
let LandPriceVerify,
  LandPriceZoneAllocation,
  LandPriceZoneAllocationSearch,
  DatumSelection,
  AnomalyCheck,
  Admin,
  ReportDownload,
  PermissionChanges
let DownloadPage, SqlQuery, EmailChangePassword, Navigation

let Backstage, DownloadListPage, ExecuteScheduleJob, ScheduleJobStatus

let LoginUnuse

let RealEstate,
  MarketOverview,
  CrossAnalysis,
  SampleDetails,
  CaseAnalysis,
  StatisticalReports

if (available) {
  Login = lazy(() => import("./components/Login/Login"))
  SimpleFilter = lazy(() => import("./components/SimpleFilter/SimpleFilter"))
  ModelCreate = lazy(() => import("./components/ModelCreate"))
  HouseLand = lazy(() => import("./components/HouseLand/HouseLand"))
  FactorAdjustmentTable = lazy(
    () => import("./components/FactorAdjustmentTable/FactorAdjustmentTable")
  )
  LandPriceInquiry = lazy(
    () => import("./components/LandPriceInquiry/LandPriceInquiry")
  )
  LandPriceSearch = lazy(
    () => import("./components/LandPriceSearch/LandPriceSearch")
  )
  LandPriceVerify = lazy(() => import("./components/LandPriceVerify"))
  LandPriceZoneAllocation = lazy(
    () => import("./components/LandPriceZoneAllocation/LandPriceZoneAllocation")
  )
  LandPriceZoneAllocationSearch = lazy(
    () =>
      import("./components/LandPriceZoneAllocationSearch/LandPriceZoneAllocationSearch")
  )
  DatumSelection = lazy(
    () => import("./components/DatumSelection/DatumSelection")
  )
  AnomalyCheck = lazy(() => import("./components/AnomalyCheck/AnomalyCheck"))
  Admin = lazy(() => import("./components/Admin/Admin"))
  ReportDownload = lazy(
    () => import("./components/ReportDownload/ReportDownload")
  )
  PermissionChanges = lazy(
    () => import("./components/AccountManagement/PermissionChanges")
  )
  DownloadPage = lazy(() => import("./components/DownloadPage"))
  SqlQuery = lazy(() => import("./components/SqlQuery"))
  EmailChangePassword = lazy(
    () => import("./components/Login/EmailChangePassword")
  )
  Navigation = lazy(() => import("./components/Navigation"))
  LoginUnuse = lazy(() => import("./components/Login/LoginUnuse")) // for驗收時做的偷偷切換頁面
  Backstage = lazy(() => import("./components/Backstage"))
  DownloadListPage = lazy(
    () => import("./components/Backstage/DownloadListPage")
  )
  ExecuteScheduleJob = lazy(
    () => import("./components/Backstage/ExecuteScheduleJob")
  )
  ScheduleJobStatus = lazy(
    () => import("./components/Backstage/ScheduleJobStatus")
  )
  MarketOverview = lazy(() => import("./components/Dashboard/MarketOverview"))
  CrossAnalysis = lazy(() => import("./components/Dashboard/CrossAnalysis"))
  SampleDetails = lazy(
    () => import("./components/Dashboard/CrossAnalysis/SampleDetails")
  )
  CaseAnalysis = lazy(() => import("./components/Dashboard/CaseAnalysis"))
  RealEstate = lazy(() => import("./components/Dashboard/RealEstate"))
  StatisticalReports = lazy(
    () => import("./components/Dashboard/StatisticalReports")
  )
} else LoginUnuse = lazy(() => import("./components/Login/LoginUnuse"))

// 從環境變數讀取 CSP nonce
const CSP_NONCE = process.env.REACT_APP_CSP_NONCE

// 創建 memoized selector 來計算用戶權限
const selectUserPermissions = createSelector(
  [(state) => state.auth.role_id, (state) => state.authList.list],
  (userRoleId, roleList) => {
    const matchList = roleList.filter(
      (value) => value.parent_function_name === "系統管理"
    )
    const hasPermissions = matchList
      .map((value) => value.r_role_id)
      .flatMap((item) => item.split(",").map(Number))
      .includes(userRoleId)
    const pageRoute = matchList.find((item) => {
      return Number(item.r_role_id) === userRoleId
    })

    return {
      hasPermissions: hasPermissions,
      pageRoute: pageRoute?.function_name
    }
  }
)

// 創建 memoized selector 來計算統計報表權限
const selectStatisticalReportsPermission = createSelector(
  [(state) => state.auth.role_id, (state) => state.authList.list],
  (userRoleId, roleList) => {
    const auth = roleList.find(
      (item) =>
        item.parent_function_name === "儀表板" &&
        item.function_name === "統計報表"
    )
    if (!auth) return false
    return String(auth.r_role_id).split(",").map(Number).includes(userRoleId)
  }
)

// 創建 memoized selector 來計算人檔與建案分析權限
const selectCaseAnalysisPermission = createSelector(
  [(state) => state.auth.role_id, (state) => state.authList.list],
  (userRoleId, roleList) => {
    const auth = roleList.find(
      (item) =>
        item.parent_function_name === "儀表板" &&
        item.function_name === "人檔與建案分析"
    )
    if (!auth) return false
    return String(auth.r_role_id).split(",").map(Number).includes(userRoleId)
  }
)

export default function App() {
  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(false)
  const onOpen = () => setIsOpen(true)
  const onClose = () => setIsOpen(false)
  const isLogin = useSelector((state) => state.auth.login)
  const isTimeout = useSelector((state) => state.auth.timeout)
  const dataYears = useSelector((state) => state.auth.data_years)
  const userPermissions = useSelector(selectUserPermissions)
  const canAccessCaseAnalysis = useSelector(selectCaseAnalysisPermission)
  const canAccessStatisticalReports = useSelector(
    selectStatisticalReportsPermission
  )
  const backstageAuth = JSON.parse(localStorage.getItem("backstage") || "{}")
  const isBackstageLogin = !!backstageAuth.token
  const closeModal = () => {
    dispatch(closeTimeoutDialog())
    onClose()
    navigate("/", { replace: true })
    window.location.reload()
  }

  useEffect(() => {
    if (location.pathname === "/changePassword") {
      clearLoginData()
      dispatch(logout())
    }
    // 當用戶導向首頁時，清除所有登入資料強制重新登入
    if (location.pathname === "/" && isLogin) {
      clearLoginData()
      dispatch(logout())
    }
    // 年度必填改造：舊版 session（登入回應無 data_years）強制重新登入取得新欄位。
    // 移除條件：確認全體使用者 access_token 皆為本次上線後核發（即後端登入
    // log 中已無 data_years 缺欄位的請求）達一個完整使用週期（例如一週）後，
    // 這段可以整段移除。
    if (isLogin && !dataYears) {
      clearLoginData()
      dispatch(logout())
    }
  }, [dispatch, location])

  // 避免app global loading卡死
  useEffect(() => {
    const auth = getAuth()
    if (auth.loading) {
      setGlobalLoadingFalse(() => {
        dispatch(closeLoading())
      })
    }
  }, [])

  useEffect(() => {
    if (isTimeout) onOpen()
  }, [isTimeout])

  // 閒置監聽與自動登出邏輯
  useEffect(() => {
    let cleanupListener
    let idleCheckInterval

    if (isLogin) {
      // 初始化活動監聽器
      cleanupListener = initActivityListener()

      // 每5秒檢查一次閒置狀態（測試期間）
      idleCheckInterval = setInterval(() => {
        console.log("🔍 執行閒置檢查...")
        if (isUserIdle()) {
          console.log("⚠️ 用戶閒置，執行登出...")
          clearLoginData()
          dispatch(logout())
          dispatch(openTimeoutDialog())
        }
      }, 5000) // 5秒檢查一次（測試用）
    }

    return () => {
      if (cleanupListener && typeof cleanupListener === "function") {
        cleanupListener()
      }
      if (idleCheckInterval) {
        clearInterval(idleCheckInterval)
      }
    }
  }, [isLogin, dispatch])
  // console.log(location.pathname);
  //! 需要登入才能進入的路徑
  const needloginPaths = [
    "simplefilter",
    "modelcreate",
    "houseland",
    "factorAdjustmentTable",
    "landPriceInquiry",
    "landPriceVerify",
    "landPriceSearch",
    "landPriceZoneAllocation",
    "landPriceZoneAllocationSearch",
    "datumSelection",
    "anomalyCheck",
    "admin",
    "reportDownload",
    "permissionChanges",
    "realEstate",
    "marketOverview",
    "crossAnalysis",
    "caseAnalysis",
    "statisticalReports"
  ]

  return (
    <ConfigProvider
      csp={{ nonce: CSP_NONCE }}
      theme={{
        components: {
          Button: {
            colorPrimary: "#17628b",
            primaryShadow: "none"
          }
        }
      }}
    >
      <ErrorBoundary
        fallbackRender={ErrorBoundaryLayout}
        onReset={(details) => {
          console.log(details)
        }}
      >
        <Suspense
          // fallback={<div>Loading...</div>}
          fallback={<LoadingContainer />}
        >
          <div className="App">
            <Helmet>
              <title>CAMA | Home</title>
              <meta name="avm" content="avm" />
              <link
                rel="icon"
                type="image/png"
                href="favicon.ico"
                sizes="16x16"
              />
            </Helmet>
            <LoadingContainer>
              {location.pathname.startsWith("/backstage") && (
                <div>
                  {!isBackstageLogin && <Navigate to="/backstage" replace />}
                  <Routes>
                    <Route path="/backstage" element={<Backstage />} />
                  </Routes>
                </div>
              )}
              {available ? ( // 判斷要使用一般網頁或是更新系統時的網頁
                <div>
                  {!isLogin ? (
                    <div>
                      {needloginPaths.includes(
                        location.pathname.split("/")[1]
                      ) && (
                        <Navigate
                          to="/"
                          state={{ prevPath: location.pathname }}
                          replace
                        />
                      )}
                      <Routes>
                        <Route path="/" element={<Login />} />
                        <Route
                          path="/changePassword"
                          element={<EmailChangePassword />}
                        />
                        <Route
                          path="/downloadPage"
                          element={<DownloadPage />}
                        />
                        <Route path="/sql_query" element={<SqlQuery />} />
                      </Routes>
                    </div>
                  ) : location.pathname === "/maintain" ? (
                    <div>
                      <Routes>
                        <Route path="/maintain" element={<LoginUnuse />} />
                      </Routes>
                    </div>
                  ) : (
                    <div>
                      {location.pathname === "/" ||
                      location.pathname.startsWith("/backstage") ? null : (
                        <Navigation />
                      )}
                      <Routes>
                        <Route path="/" element={<Login />} />
                        {/* <Route path="/" element={<LoginUnuse />} /> */}
                        <Route
                          path="/simplefilter"
                          element={<SimpleFilter />}
                        />
                        <Route path="/modelcreate" element={<ModelCreate />} />
                        <Route path="/houseland" element={<HouseLand />} />
                        <Route
                          path="/factorAdjustmentTable"
                          element={<FactorAdjustmentTable />}
                        />
                        <Route
                          path="/landPriceInquiry"
                          element={<LandPriceInquiry />}
                        />
                        <Route
                          path="/landPriceSearch"
                          element={<LandPriceSearch />}
                        />
                        <Route
                          path="/landPriceVerify"
                          element={<LandPriceVerify />}
                        />
                        <Route
                          path="/landPriceZoneAllocation"
                          element={<LandPriceZoneAllocation />}
                        />
                        <Route
                          path="/landPriceZoneAllocationSearch"
                          element={<LandPriceZoneAllocationSearch />}
                        />
                        <Route
                          path="/datumSelection"
                          element={<DatumSelection />}
                        />
                        <Route
                          path="/anomalyCheck"
                          element={<AnomalyCheck />}
                        />
                        <Route
                          path="/admin"
                          element={
                            !userPermissions.hasPermissions ? (
                              <Navigate to="/simplefilter" replace />
                            ) : (
                              <Admin userPermissions={userPermissions} />
                            )
                          }
                        />
                        <Route
                          path="/reportDownload"
                          element={<ReportDownload />}
                        />
                        <Route
                          path="/permissionChanges"
                          element={<PermissionChanges />}
                        />
                        <Route
                          path="/downloadPage"
                          element={<DownloadPage />}
                        />
                        <Route path="/sql_query" element={<SqlQuery />} />
                        <Route
                          path="/marketOverview"
                          element={<MarketOverview />}
                        />
                        <Route
                          path="/crossAnalysis"
                          element={<CrossAnalysis />}
                        />
                        <Route
                          path="/crossAnalysis/sampleDetails"
                          element={<SampleDetails />}
                        />
                        <Route
                          path="/caseAnalysis"
                          element={
                            !canAccessCaseAnalysis ? (
                              <Navigate to="/simplefilter" replace />
                            ) : (
                              <CaseAnalysis />
                            )
                          }
                        />
                        <Route path="/realEstate" element={<RealEstate />} />
                        <Route
                          path="/statisticalReports"
                          element={
                            !canAccessStatisticalReports ? (
                              <Navigate to="/simplefilter" replace />
                            ) : (
                              <StatisticalReports />
                            )
                          }
                        />
                      </Routes>
                    </div>
                  )}
                  <Modal
                    open={isOpen}
                    onCancel={closeModal}
                    title={
                      <div style={{ fontSize: "20px", marginBottom: "20px" }}>
                        連線逾時
                      </div>
                    }
                    footer={[
                      <Button key="confirm" type="primary" onClick={closeModal}>
                        確認
                      </Button>
                    ]}
                  >
                    待機過久，請重新登入！
                  </Modal>
                </div>
              ) : (
                <Routes>
                  <Route path="/" element={<LoginUnuse />} />
                </Routes>
              )}
            </LoadingContainer>
          </div>
        </Suspense>
      </ErrorBoundary>
    </ConfigProvider>
  )
}
